/**
 * PKCE Service - Simplified for Backend Proxy Architecture
 *
 * This service provides a clean interface for PKCE OAuth2 authentication
 * through your backend proxy. It handles the PKCE flow by communicating
 * with your backend API instead of ZITADEL directly.
 */

import { z } from 'zod';
import {
  getPermissionsFromGroups,
  isAdminFromGroups,
} from './jwt-utils';
import { User, UserProfile } from '../../../shared/auth';
import { env } from '../../../shared/utils/env';

const AuthorizationUrlResponseSchema = z
  .object({
    authorizationUrl: z.string(),
    codeVerifier: z.string().optional(),
  })
  .passthrough();

interface TokenData {
  sub?: string;
  id?: string;
  email?: string;
  username?: string;
  preferred_username?: string;
  given_name?: string;
  first_name?: string;
  family_name?: string;
  last_name?: string;
  groups?: string[];
  created_at?: string | number | Date;
  updated_at?: string | number | Date;
}

/**
 * PKCE Authentication Service
 *
 * This service provides utilities for PKCE OAuth2 operations through the backend proxy.
 * The actual authentication is handled by the AuthProvider component and AuthContext.
 */
export class PKCEService {
  /**
   * Get the AuthProvider configuration for backend proxy
   * This is used by the PKCEAuthProvider component
   */
  getAuthConfig() {
    // Use environment variables with fallbacks for both browser and test environments
    const backendUrl = env.PERIVERSE_AUTH_API_URL;
    const frontendRedirectUri = env.REDIRECT_URI;
    const frontendPostLogoutRedirectUri = env.FRONTEND_URL;

    return {
      clientId: 'backend-proxy', // Backend handles client ID
      authorizationEndpoint: `${backendUrl}/auth/pkce/authorize`,
      tokenEndpoint: `${backendUrl}/auth/pkce/callback`,
      redirectUri: `${backendUrl}/auth/callback`, // Backend callback (ZITADEL redirects here)
      scope: 'openid profile email offline_access',
      logoutEndpoint: `${backendUrl}/auth/logout`,
      logoutRedirect: frontendPostLogoutRedirectUri, // Frontend post-logout redirect
      decodeToken: true,
      autoLogin: false,
      clearURL: true,
      storage: 'session' as const,
      tokenRequestCredentials: 'include' as const,
      // Additional parameters for backend to know where to redirect after auth
      extraAuthParameters: {
        frontend_redirect_uri: frontendRedirectUri,
      },
    };
  }

  /**
   * Get the authorization URL for login
   * This calls the backend API to get the ZITADEL authorization URL
   */
  async getAuthorizationUrl(frontendRedirectUri: string): Promise<string> {
    const authConfig = this.getAuthConfig();
    const params = new URLSearchParams({
      redirect_uri: frontendRedirectUri,
      response_type: 'code',
      scope: authConfig.scope,
      frontend_redirect_uri: frontendRedirectUri,
    });

    try {
      console.log('PKCEService: Calling backend for authorization URL...', {
        endpoint: authConfig.authorizationEndpoint,
        params: params.toString(),
      });

      const response = await fetch(
        `${authConfig.authorizationEndpoint}?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(
          `Backend API error: ${response.status} ${response.statusText}`
        );
      }

      const data = AuthorizationUrlResponseSchema.parse(
        await response.json()
      );
      console.log(
        'PKCEService: Received authorization URL from backend:',
        data
      );

      // Store the code verifier returned by the backend
      if (typeof window !== 'undefined' && data.codeVerifier) {
        sessionStorage.setItem('pkce_code_verifier', data.codeVerifier);
        console.log(
          '🔍 PKCE SERVICE DEBUG - Stored codeVerifier from backend:',
          data.codeVerifier
        );
      } else {
        console.error(
          '🔍 PKCE SERVICE DEBUG - No codeVerifier received from backend!'
        );
      }

      return data.authorizationUrl;
    } catch (error) {
      console.error('PKCEService: Failed to get authorization URL:', error);
      throw new Error(
        `Failed to get authorization URL: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Get the logout URL
   * This constructs the backend logout URL
   */
  getLogoutUrl(): string {
    const authConfig = this.getAuthConfig();
    const params = new URLSearchParams({
      post_logout_redirect_uri: authConfig.logoutRedirect,
    });

    return `${authConfig.logoutEndpoint}?${params.toString()}`;
  }

  /**
   * Convert ZITADEL user info to our User interface
   * This maps the OAuth2 user info to our internal user structure
   */
  mapUserFromTokenData(tokenData: TokenData | null | undefined): User | null {
    if (!tokenData) {
      return null;
    }

    try {
      const id = tokenData.sub || tokenData.id;
      const username =
        tokenData.preferred_username || tokenData.username || tokenData.email;

      if (!id || !username) {
        return null;
      }

      const groups = tokenData.groups ?? [];
      const permissions = getPermissionsFromGroups(groups);
      const isAdmin = isAdminFromGroups(groups);

      const user: User = {
        id,
        username,
        permissions,
        isAdmin,
        isActive: true,
        inactivityLimitMs: null,
        sessionKey: null,
      };

      console.log('PKCEService: Mapped token data to User:', user);

      return user;
    } catch (error) {
      console.error('PKCEService: Failed to map user from token data:', error);
      return null;
    }
  }

  /**
   * Convert ZITADEL user info to our UserProfile interface
   * This provides extended user information
   */
  mapUserProfileFromTokenData(
    tokenData: TokenData | null | undefined
  ): UserProfile | null {
    if (!tokenData) {
      return null;
    }

    try {
      const id = tokenData.sub || tokenData.id;
      const username =
        tokenData.preferred_username || tokenData.username || tokenData.email;
      const email = tokenData.email || username;

      if (!id || !username || !email) {
        return null;
      }

      const groups = tokenData.groups ?? [];
      const permissions = getPermissionsFromGroups(groups);
      const isAdmin = isAdminFromGroups(groups);

      const userProfile: UserProfile = {
        id,
        username,
        permissions,
        isAdmin,
        isActive: true,
        inactivityLimitMs: null,
        sessionKey: null,
        email,
        firstName: tokenData.given_name || tokenData.first_name || '',
        lastName: tokenData.family_name || tokenData.last_name || '',
        createdAt: tokenData.created_at
          ? new Date(tokenData.created_at)
          : new Date(),
        updatedAt: tokenData.updated_at
          ? new Date(tokenData.updated_at)
          : new Date(),
        lastLogin: new Date(), // Current login time
      };

      console.log(
        'PKCEService: Mapped token data to UserProfile:',
        userProfile
      );

      return userProfile;
    } catch (error) {
      console.error(
        'PKCEService: Failed to map user profile from token data:',
        error
      );
      return null;
    }
  }

}

// Export singleton instance
export const pkceService = new PKCEService();
